import anyLeague from "../../classificators/league/anyLeague.js";
import pitchingScopeContext from "../../classificators/resultsSlice/pitchingScopeContext.js";
import resultsSliceForPitcher from "../../classificators/resultsSlice/resultsSliceForPitcher.js";
import satisfiesAll from "../../classificators/satisfiesAll.js";
import sessionClassifierBuilder from "../../classificators/session/sessionClassifierBuilder.js";
import gameOnly from "../../classificators/sessionType/gameOnly.js";
import { IterableDataProcessor, RankingStat } from "../../entities/index.js";
import NumberFormatter from "../../formatters/NumberFormatter.js";
import RankingStatCaseVerifier from "../../validators/RankingStatCaseVerifier.js";
import MultiRankingStatCalculator from "./MultiRankingStatCalculator.js";
import EnumValueProvider from "../../providers/EnumValueProvider.js";
import { DataSection, PitchingRankingStats, PitchData, ResultDataNew, GameRule } from "../../enums/index.js";
import sessionCouldHaveScoringData from './../../classificators/sessionContents/sessionCouldHaveScoringData.js';
import sessionCouldHaveNewScoringData from './../../classificators/sessionContents/sessionCouldHaveNewScoringData.js';
import satisfiesAny from './../../../src/classificators/satisfiesAny.js';

class PitchingRecordCalculator extends MultiRankingStatCalculator {
    constructor(customResultsProcessor, rankingStatCaseVerifier, numberFormatter, skipCache) {
        if (typeof(numberFormatter) !== 'undefined' && !(numberFormatter instanceof NumberFormatter)) {
            throw new TypeError('numberFormatter must be an instance inheriting from NumberFormatter class');
        }
        if (typeof(customResultsProcessor) !== 'undefined' && !(customResultsProcessor instanceof IterableDataProcessor)) {
            throw new TypeError('customResultsProcessor must be an instance inheriting from IterableDataProcessor class');
        }

        super(
            [ PitchingRankingStats.Wins, PitchingRankingStats.Looses, PitchingRankingStats.WinningPercentage ],
            rankingStatCaseVerifier || new RankingStatCaseVerifier(
                anyLeague,
                sessionClassifierBuilder(
                    gameOnly,
                    satisfiesAny(sessionCouldHaveScoringData, sessionCouldHaveNewScoringData)
                ),
                satisfiesAll(pitchingScopeContext, resultsSliceForPitcher)
            ),
            skipCache
        );

        this.numberFormatter = numberFormatter || new NumberFormatter(0);
        this.resultsProcessor = customResultsProcessor || new IterableDataProcessor(
            new EnumValueProvider()
        );
        this.inningNrPath = ResultDataNew.getDataPath(
            PitchData.BeforePlayInning.key, DataSection.Scoring.key
        );
        this.inningIsTopPath = ResultDataNew.getDataPath(
            PitchData.BeforePlayInningIsTop.key, DataSection.Scoring.key
        );
        this.totalRunsPath = ResultDataNew.getDataPath(
            PitchData.AfterPlayTotalRuns.key, DataSection.Scoring.key
        );
    }

    /**
     * Calculates given statistical parameter when it is not avaialble in cache
     * @param {ResultsSlice} slice - result slice with scope
     * @param {object} session - session object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/session-v2.json
     * @param {object} league - league object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/league.json
     * @returns {object} object with keys equal to ranking stat parameter name and RankingStat instance as value
     */
    forceCalculate(slice, session, league) {
        const output = {};

        const pitcherId = slice.scope.tagId;
        const results = slice.getBiggestSlice();
        const inningsRequiredForStarting = this.determineMinInningsForStartingPitcher(session);
        const home = this.buildTeamDataTemplate();
        const away = this.buildTeamDataTemplate();

        // @see https://www.mlb.com/glossary/standard-stats/win
        // @see https://baseball.fandom.com/wiki/Win
        // A pitcher receives a win when he is the pitcher of record when his team takes the lead for good -- with a couple rare exceptions.
        // First, a starting pitcher must pitch at least five innings (in a traditional game of nine innings or longer) to qualify for the win. If he does not, the official scorer awards the win to the most effective relief pitcher.

        // @see https://www.mlb.com/glossary/standard-stats/loss
        // @see https://baseball.fandom.com/wiki/Loss
        // A pitcher receives a loss when a run that is charged to him proves to be the go-ahead run in the game, giving the opposing team a lead it never gives up.
        for (let i = 0; i < results.length; i++) {
            const result = results[i];

            const inningIsTop = this.resultsProcessor.getObjectContent(
                result, this.inningIsTopPath
            );

            if (typeof(inningIsTop) === 'undefined' || Number.isNaN(inningIsTop)) {
                // TODO - maybe throw ? this could end processing quickly for non tagged data
                continue;
            }

            // @see https://sports.stackexchange.com/questions/857/what-are-the-criteria-for-wins-losses-and-saves-in-mlb
            // @see https://www.reddit.com/r/baseball/comments/1fy0lt/what_determines_a_win_or_loss_for_a_pitcher/caewto5?utm_source=share&utm_medium=web2x&context=3
            if (inningIsTop == 1) {
                // away team at bat (offense), home team is fielding (defense & pitching)
                this.analyzeInningHalf(result, away, home, inningsRequiredForStarting);
            }
            else {
                // home team at bat (offense), away team is fielding (defense & pitching)
                this.analyzeInningHalf(result, home, away, inningsRequiredForStarting);
            }
        }

        const winParam = this.supportedParams[0];
        let winningPercentage = null;
        if (
            (home.tookLeadWith && home.tookLeadWith === pitcherId)
            || (away.tookLeadWith && away.tookLeadWith === pitcherId)
        ) {
            output[winParam.key] = RankingStat.fromParameter(
                winParam,
                this.numberFormatter.format(1),
                1
            );

            winningPercentage = 100;
        }

        const lossParam = this.supportedParams[1];
        if (
            (home.droppedLeadWith && home.droppedLeadWith === pitcherId)
            || (away.droppedLeadWith && away.droppedLeadWith === pitcherId)
        ) {
            output[lossParam.key] = RankingStat.fromParameter(
                lossParam,
                this.numberFormatter.format(1),
                1
            );

            winningPercentage = 0;
        }

        if (winningPercentage != null) {
            const winningPercentageParam = this.supportedParams[2];
            output[winningPercentageParam.key] = RankingStat.fromParameter(
                winningPercentageParam,
                winningPercentage,
                1
            );
        }

        return output;
    }

    analyzeInningHalf(result, battingTeam, fieldingTeam, inningsRequiredForStarting) {
        const inningNumber = this.resultsProcessor.getObjectContent(
            result, this.inningNrPath
        );
        if (typeof(inningNumber) === 'undefined' || Number.isNaN(inningNumber)) {
            // TODO - maybe silently continue ? On the other hand we have inningIsTop but no InningNr ?
            throw new Error(`Inning number for result ${result[ResultDataNew.Id.key]} is 0 or NaN`);
        }

        // check the score
        // TODO - this could also be based on HOME_TEAM_SCORE and AWAY_TEAM_SCORE for new apps
        const battingTeamScoreAfterPitch = this.resultsProcessor.getObjectContent(
            result, this.totalRunsPath
        );

        if (typeof(battingTeamScoreAfterPitch) === 'undefined' || Number.isNaN(battingTeamScoreAfterPitch)) {
            // incomplete scoring ?
            return;
        }

        const pitchedById = result[ResultDataNew.PitcherId.key];
        // fielding team can tie or drop the lead, batting team can tie or take the lead
        if (fieldingTeam.startingPitcher === null) {
            fieldingTeam.startingPitcher = pitchedById;
        }
        if (fieldingTeam.startingPitcher === pitchedById) {
            // starting pitcher continues
            // TODO - this will not work very well if apps will allow to re-enter substituted players
            fieldingTeam.startingPitcherCompletedInnings = Number.parseInt(inningNumber, 10);
        }

        fieldingTeam.pitcher = pitchedById;
        // TODO - this is not the most elegant way, but if fieldingTeam.tookLeadWith or fieldingTeam.droppedLeadWith is equal to 0 (int) and not null, we must inject starting pitcher here
        if (fieldingTeam.tookLeadWith === 0) {
            fieldingTeam.tookLeadWith = fieldingTeam.pitcher;
        }
        else if (fieldingTeam.droppedLeadWith === 0) {
            fieldingTeam.droppedLeadWith = fieldingTeam.pitcher;
        }

        // check if the team which is currently in offense is taking a lead
        if (
            battingTeamScoreAfterPitch > fieldingTeam.score
            && (
                battingTeam.score <= fieldingTeam.score // this case if obvious, team at bat took the lead
                || battingTeam.tookLeadWith == null // team at bat is winning but pitcher wasn't set before - for example starting pitcher and min nr of innings
                || fieldingTeam.droppedLeadWith == null
            )
        ) {
            // @see https://www.mlb.com/glossary/standard-stats/win
            // before we can set winning and loosing pitcher, we need to check required number of *COMPLETED* innings
            if (battingTeam.pitcher !== battingTeam.startingPitcher || battingTeam.startingPitcherCompletedInnings >= inningsRequiredForStarting) {
                battingTeam.tookLeadWith = battingTeam.pitcher || 0; // NOTE - battingTeam.pitcher may not be set yet!
            }
            else {
                battingTeam.tookLeadWith = null;
            }
            battingTeam.droppedLeadWith = null;

            // so the fielding team is loosing
            // @see https://www.mlb.com/glossary/standard-stats/loss
            fieldingTeam.tookLeadWith = null;
            // before we can set winning and loosing pitcher, we need to check required number of *COMPLETED* innings.
            if (fieldingTeam.pitcher !== fieldingTeam.startingPitcher || fieldingTeam.startingPitcherCompletedInnings > inningsRequiredForStarting) {
                fieldingTeam.droppedLeadWith = fieldingTeam.pitcher;
            }
            else {
                fieldingTeam.droppedLeadWith = null;
            }
        }
        // or if they are going to loose, this actually shouldn't happen, it means that we have negative points here!
        else if (battingTeam.score >= fieldingTeam.score && battingTeamScoreAfterPitch < fieldingTeam.score) {
            throw new Error(`Batting team's score for result ${result[ResultDataNew.Id.key]} has been decreased!`);
            // battingTeam.droppedLeadWith = battingTeam.pitcher || 0; // NOTE - battingTeam.pitcher may not be set yet!
            // battingTeam.tookLeadWith = null;
        }
        // if they are tied... reset both
        else if (battingTeamScoreAfterPitch === fieldingTeam.score) {
            battingTeam.tookLeadWith = null;
            battingTeam.droppedLeadWith = null;
            fieldingTeam.tookLeadWith = null;
            fieldingTeam.droppedLeadWith = null
        }

        battingTeam.score = battingTeamScoreAfterPitch;
    }

    determineMinInningsForStartingPitcher(session) {
        // a starting pitcher must pitch at least five innings (in a traditional game of nine innings or longer) to qualify for the win.
        // If he does not, the official scorer awards the win to the most effective relief pitcher.
        let  inningsRequiredForStarting = 5;
        if (session && session.rules && session.rules[GameRule.INNINGS] && !Number.isNaN(session.rules[GameRule.INNINGS])) {
            // TODO - this can be not always true, but if in 9 innings games he needs to pitch at least 5, it means more than a half
            inningsRequiredForStarting = Math.ceil(session.rules[GameRule.INNINGS] / 2);
        }

        return inningsRequiredForStarting;
    }

    buildTeamDataTemplate() {
        return {
            score: 0,
            startingPitcher: null,
            startingPitcherCompletedInnings: 0,
            tookLeadWith: null,
            droppedLeadWith: null,
            pitcher: null,
        };
    }
}

export default PitchingRecordCalculator;